import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { setUnauthorizedHandler } from "./api/client";
import { LandingLayout } from "./components/LandingLayout";
import { Layout } from "./components/Layout";
import { RequireAuth } from "./components/RequireAuth";
import { AboutPage } from "./pages/AboutPage";
import { AuthPage } from "./pages/AuthPage";
import { ConfirmEmailPage } from "./pages/ConfirmEmailPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { JoinPage } from "./pages/JoinPage";
import { LandingPage } from "./pages/LandingPage";
import { LeaderboardPage } from "./pages/LeaderboardPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { ProfilePage } from "./pages/ProfilePage";
import { QuestDetailPage } from "./pages/QuestDetailPage";
import { QuestListPage } from "./pages/QuestListPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { SharedResultPage } from "./pages/SharedResultPage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";
import { Spinner } from "./components/ui";
import { CookieNotice } from "./components/CookieNotice";
import { DeletionPendingDialog } from "./components/DeletionPendingDialog";
import { useAuthStore } from "./stores/authStore";

// Экраны с картой тянут MapLibre — около мегабайта JavaScript. Грузим их
// отдельным чанком, чтобы лендинг и каталог открывались без него.
const PlayPage = lazy(() =>
  import("./pages/PlayPage").then((module) => ({ default: module.PlayPage })),
);
const ResultsPage = lazy(() =>
  import("./pages/ResultsPage").then((module) => ({ default: module.ResultsPage })),
);
const CreateQuestPage = lazy(() =>
  import("./pages/CreateQuestPage").then((module) => ({ default: module.CreateQuestPage })),
);

/**
 * Что показать на корневом адресе.
 *
 * Вошедшему продавать игру незачем — он идёт сразу в каталог. Пока сессия
 * восстанавливается, не показываем ничего: иначе первый экран успел бы
 * мигнуть и смениться каталогом.
 */
function Home() {
  const status = useAuthStore((state) => state.status);

  if (status === "loading") return null;
  if (status === "authenticated") return <Navigate to="/quests" replace />;
  return <LandingPage />;
}

export default function App() {
  const restore = useAuthStore((state) => state.restore);
  const clear = useAuthStore((state) => state.clear);

  useEffect(() => {
    // Access-токен живёт только в памяти, поэтому после перезагрузки страницы
    // сессия восстанавливается по httpOnly refresh-cookie.
    void restore();

    // Если обновить токен не удалось, клиент API сообщает об этом сюда,
    // и интерфейс переключается в анонимное состояние.
    setUnauthorizedHandler(clear);
    return () => setUnauthorizedHandler(null);
  }, [restore, clear]);

  return (
    <BrowserRouter>
      <Suspense fallback={<Spinner />}>
      <Routes>
        {/* Первый экран живёт в своём каркасе: ни навигации по разделам, ни
            футера — всё, кроме «Играть», уводит от единственного действия,
            ради которого человек сюда попал. */}
        <Route element={<LandingLayout />}>
          <Route index element={<Home />} />
        </Route>

        <Route element={<Layout />}>
          <Route path="login" element={<AuthPage mode="login" />} />
          <Route path="register" element={<AuthPage mode="register" />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          {/* Ссылка из письма: открывается и без входа, в том числе в другом браузере. */}
          <Route path="reset-password" element={<ResetPasswordPage />} />
          {/* Ссылка из письма: открывается и без входа, в том числе в другом браузере. */}
          <Route path="confirm-email" element={<ConfirmEmailPage />} />
          {/* Ссылка «поделиться»: открывается и без аккаунта, но играть без
              входа нельзя — это решает страница квеста. */}
          <Route path="shared/:sessionId" element={<SharedResultPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          {/* Ссылка из письма: открывается и без входа, в том числе в другом браузере. */}
          <Route path="verify-email" element={<VerifyEmailPage />} />
          <Route path="quests" element={<QuestListPage />} />
          <Route path="quests/:questId" element={<QuestDetailPage />} />
          <Route path="players/:userId" element={<ProfilePage />} />

          {/* Всё, что меняет состояние игры, доступно только авторизованным. */}
          <Route element={<RequireAuth />}>
            {/* Лидерборд — только для своих: гость попадает на вход. */}
            <Route path="leaderboard" element={<LeaderboardPage />} />
            <Route path="profile" element={<ProfilePage />} />
            {/* Избранное — вкладка профиля со своим адресом: на неё ведут
                из меню под аватаркой, и человек должен попадать сразу туда. */}
            <Route path="favorites" element={<ProfilePage />} />
            <Route path="create" element={<CreateQuestPage />} />
            {/* Та же форма правит существующий квест: поля и проверки общие,
                а две копии разъехались бы с первой же правки. */}
            <Route path="quests/:questId/edit" element={<CreateQuestPage />} />
            <Route path="join" element={<JoinPage />} />
            <Route path="join/:joinCode" element={<JoinPage />} />
            <Route path="play/:sessionId" element={<PlayPage />} />
            {/* Лобби как отдельная страница исчезло вместе с прежней схемой
                «комната = партия»: комната теперь живёт в шапке и переживает
                любую игру. Старые ссылки уводят в каталог, где она видна. */}
            <Route path="sessions/:sessionId/lobby" element={<Navigate to="/quests" replace />} />
            <Route path="sessions/:sessionId/results" element={<ResultsPage />} />
          </Route>

          <Route path="quest/:questId" element={<Navigate to="/quests" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
      <CookieNotice />
      <DeletionPendingDialog />
      </Suspense>
    </BrowserRouter>
  );
}
